/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/**
 * Webview detection.
 *
 * The portal is embedded inside the native Grab Merchant app's webview.
 * The User-Agent is the source of truth: the native app appends a `Grab
 * Merchant/<x.y.z>` token to the webview UA (a production contract also relied
 * on by mex-mobile-web), which is present synchronously on every load and every
 * client-side route — so no caching is needed to survive navigation. The
 * `?webview=true` query param is only a manual override so app-context behaviour
 * can be exercised in a desktop browser, where the native UA can't be faked.
 *
 * This answers "are we inside the app", nothing more. It gates **app-context**
 * behaviour only — hiding the sidebar and features the app does not offer. The
 * mobile *design* is width-driven (Duxton breakpoints), and the page header,
 * title and back control render in every context: the webview is full screen and
 * the app supplies no nav bar. See `.claude/reference/ecommerce-mobile-webview.md`.
 */
export const WEBVIEW_QUERY_PARAM = "webview"
export const WEBVIEW_BODY_CLASS = "webview-mode"

// The native Grab Merchant app appends a "Grab Merchant/<version>" token to the
// webview User-Agent. We require the "/" delimiter followed by a version digit
// (so neither a stray "Grab Merchant" nor a version-less "Grab Merchant/" can
// false-positive) but deliberately do NOT pin the full version shape — we only
// need presence, and matching "\d+\.\d+\.\d+" would break detection if the app
// ever ships a differently-formatted version.
const GRAB_MERCHANT_UA = /Grab\s+Merchant\/\d/i

const readFromUserAgent = (): boolean => {
  try {
    return GRAB_MERCHANT_UA.test(window.navigator.userAgent)
  } catch {
    return false
  }
}

const readFromQuery = (): boolean => {
  try {
    return (
      new URLSearchParams(window.location.search).get(WEBVIEW_QUERY_PARAM) ===
      "true"
    )
  } catch {
    return false
  }
}

/**
 * Returns true when the portal is running inside the native app webview.
 * Resolved fresh from the UA (source of truth), or the `?webview=true` override
 * used for desktop testing.
 */
export const isWebview = (): boolean => {
  if (typeof window === "undefined") return false
  return readFromUserAgent() || readFromQuery()
}

/**
 * Adds the `webview-mode` class to <body> when running in the webview, so
 * global/layout CSS can switch to the mobile design. Safe to call at bootstrap.
 */
export const applyWebviewBodyClass = (): void => {
  if (typeof document === "undefined") return
  if (isWebview()) {
    document.body.classList.add(WEBVIEW_BODY_CLASS)
  }
}

/**
 * The `ecom-page` body class lets global CSS release the desktop content-width
 * floor so eCom pages are RESPONSIVE at any width (see `body.ecom-page` in
 * styles/global.ts). Unlike the webview class, this is driven by the ROUTE, not by
 * the app context — call it at bootstrap and on every client-side route change with
 * whether the current path is an eCom route (see `isEcomPath` in constants/routes).
 */
export const ECOM_PAGE_BODY_CLASS = "ecom-page"

export const applyEcomPageBodyClass = (isEcomPage: boolean): void => {
  if (typeof document === "undefined") return
  document.body.classList.toggle(ECOM_PAGE_BODY_CLASS, isEcomPage)
}

/**
 * The `ecom-full-bleed` body class releases the shell's centred content-width CAP
 * (`max-width`), letting a page's header band and sticky action bar run edge to
 * edge while the page keeps its own inner cap on the readable content. Route-driven
 * like `ecom-page`, and deliberately narrower than it — see `isEcomFullBleedPath` in
 * constants/routes.
 */
export const ECOM_FULL_BLEED_BODY_CLASS = "ecom-full-bleed"

export const applyEcomFullBleedBodyClass = (isFullBleed: boolean): void => {
  if (typeof document === "undefined") return
  document.body.classList.toggle(ECOM_FULL_BLEED_BODY_CLASS, isFullBleed)
}
